import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Search, User, Check, ArrowLeft, Send, AlertCircle, 
  Clock, CheckCircle, XCircle, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function ParentRegistration() {
  const [step, setStep] = useState('search'); // search, form, submitted, check-status
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [config, setConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [requestStatus, setRequestStatus] = useState(null);
  
  const [formData, setFormData] = useState({
    parent_first_name: '',
    parent_last_name: '',
    email: '',
    phone: '',
    relationship: '',
    relationship_other: '',
    notes: ''
  });

  useEffect(() => {
    loadConfig();
    
    // Check for saved request ID
    const savedRequestId = localStorage.getItem('parent_link_request_id');
    if (savedRequestId) {
      setRequestId(savedRequestId);
    }
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/parent-linking/config`);
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to load config:', err);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/parent-linking/search-child`, {
        params: { query: searchQuery }
      });
      setSearchResults(response.data.results);
      
      if (response.data.results.length === 0) {
        toast.info('No athletes found matching your search');
      }
    } catch (err) {
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const selectChild = (child) => {
    setSelectedChild(child);
    setStep('form');
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.parent_first_name || !formData.parent_last_name) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.email) {
      toast.error('Please enter your email');
      return;
    }
    if (!formData.phone) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.relationship) {
      toast.error('Please select your relationship');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/parent-linking/request`, {
        child_id: selectedChild.id,
        ...formData
      });
      
      setRequestId(response.data.request_id);
      localStorage.setItem('parent_link_request_id', response.data.request_id);
      setStep('submitted');
      toast.success('Request submitted successfully!');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to submit request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = async () => {
    if (!requestId) {
      toast.error('Please enter a request ID');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/parent-linking/request/${requestId}/status`);
      setRequestStatus(response.data);
      setStep('check-status');
    } catch (err) {
      toast.error('Request not found');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'rejected': return <XCircle className="h-8 w-8 text-red-500" />;
      case 'info_requested': return <AlertCircle className="h-8 w-8 text-yellow-500" />;
      default: return <Clock className="h-8 w-8 text-[#02b5ff]" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Not Approved';
      case 'info_requested': return 'Additional Information Required';
      default: return 'Pending Review';
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-white uppercase tracking-tight">
            Parent Registration
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Link your account to your child's athlete profile
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Search Step */}
        {step === 'search' && (
          <div className="space-y-8" data-testid="parent-search-step">
            <div className="text-center">
              <div className="w-20 h-20 bg-[#02b5ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-10 w-10 text-[#02b5ff]" />
              </div>
              <h2 className="text-xl font-bold text-white">Find Your Child</h2>
              <p className="text-zinc-400 mt-2">Search by name or athlete code</p>
            </div>
            
            <div className="flex gap-3">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Enter child's name or code..."
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                data-testid="child-search-input"
              />
              <Button
                onClick={handleSearch}
                disabled={searching}
                className="bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white px-6"
                data-testid="search-btn"
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>
            
            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-3">
                <p className="text-sm text-zinc-400">{searchResults.length} result(s) found</p>
                {searchResults.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => selectChild(child)}
                    className="w-full p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl text-left hover:border-[#02b5ff]/50 transition-all"
                    data-testid={`child-result-${child.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{child.name}</p>
                        <p className="text-sm text-zinc-500">
                          {child.age && `Age ${child.age}`}
                          {child.team && ` • ${child.team}`}
                        </p>
                      </div>
                      <div className="text-[#02b5ff]">Select →</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Check existing request */}
            <div className="border-t border-zinc-800 pt-8 mt-8">
              <p className="text-sm text-zinc-400 mb-3">Already submitted a request?</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={requestId}
                  onChange={(e) => setRequestId(e.target.value)}
                  placeholder="Enter request ID..."
                  className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                />
                <Button
                  variant="outline"
                  onClick={checkStatus}
                  className="border-zinc-700 text-zinc-300 hover:text-white"
                >
                  Check Status
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Form Step */}
        {step === 'form' && (
          <div className="space-y-6" data-testid="parent-form-step">
            <button
              onClick={() => setStep('search')}
              className="flex items-center text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to search
            </button>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-sm text-zinc-400">Registering as parent of:</p>
              <p className="text-lg font-bold text-white">{selectedChild?.name}</p>
              {selectedChild?.team && (
                <p className="text-sm text-zinc-500">{selectedChild.team}</p>
              )}
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white">Your Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">First Name *</label>
                  <input
                    type="text"
                    value={formData.parent_first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, parent_first_name: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                    data-testid="parent-first-name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Last Name *</label>
                  <input
                    type="text"
                    value={formData.parent_last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, parent_last_name: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                    data-testid="parent-last-name"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Email Address *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                    placeholder="your@email.com"
                    data-testid="parent-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Phone Number *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                    placeholder="+44 7xxx xxx xxx"
                    data-testid="parent-phone"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Relationship to Child *</label>
                <div className="grid grid-cols-3 gap-3">
                  {config?.relationships?.map((rel) => (
                    <button
                      key={rel.id}
                      onClick={() => setFormData(prev => ({ ...prev, relationship: rel.id }))}
                      className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                        formData.relationship === rel.id
                          ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                          : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                      }`}
                    >
                      {rel.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {formData.relationship === 'other' && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Please specify</label>
                  <input
                    type="text"
                    value={formData.relationship_other}
                    onChange={(e) => setFormData(prev => ({ ...prev, relationship_other: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Additional Notes (Optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none resize-none h-24"
                  placeholder="Any additional information..."
                  data-testid="parent-notes"
                />
              </div>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white py-6"
              data-testid="submit-request-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Registration Request'}
              <Send className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Submitted Step */}
        {step === 'submitted' && (
          <div className="text-center space-y-6" data-testid="parent-submitted-step">
            <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <Check className="h-12 w-12 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-white">Request Submitted!</h2>
            <p className="text-zinc-400 max-w-md mx-auto">
              Your registration request has been submitted. Our team will review it and you'll receive an email notification once it's processed.
            </p>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 inline-block">
              <p className="text-sm text-zinc-400">Your Request ID</p>
              <p className="text-lg font-mono text-white">{requestId.slice(0, 8)}</p>
            </div>
            
            <div className="pt-6">
              <Button
                onClick={() => {
                  setStep('search');
                  setSelectedChild(null);
                  setSearchResults([]);
                  setSearchQuery('');
                  setFormData({
                    parent_first_name: '',
                    parent_last_name: '',
                    email: '',
                    phone: '',
                    relationship: '',
                    relationship_other: '',
                    notes: ''
                  });
                }}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-white"
              >
                Register Another Child
              </Button>
            </div>
          </div>
        )}

        {/* Check Status Step */}
        {step === 'check-status' && requestStatus && (
          <div className="text-center space-y-6" data-testid="parent-status-step">
            <button
              onClick={() => setStep('search')}
              className="flex items-center text-zinc-400 hover:text-white transition-colors mx-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </button>
            
            <div className="w-24 h-24 bg-zinc-900 rounded-full flex items-center justify-center mx-auto">
              {getStatusIcon(requestStatus.status)}
            </div>
            
            <h2 className="text-2xl font-bold text-white">{getStatusText(requestStatus.status)}</h2>
            
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-left max-w-md mx-auto">
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-zinc-400">Child</p>
                  <p className="text-white font-medium">{requestStatus.child_name}</p>
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Submitted</p>
                  <p className="text-white">{new Date(requestStatus.created_at).toLocaleDateString()}</p>
                </div>
                {requestStatus.info_requested && requestStatus.info_request_message && (
                  <div className="border-t border-zinc-800 pt-3 mt-3">
                    <p className="text-sm text-yellow-500 font-medium">Information Required:</p>
                    <p className="text-zinc-300 text-sm mt-1">{requestStatus.info_request_message.message}</p>
                  </div>
                )}
              </div>
            </div>
            
            {requestStatus.status === 'approved' && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 max-w-md mx-auto">
                <p className="text-green-400">
                  🎉 Your account has been linked! You can now log in to view your child's data.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
